import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Avatar,
  Collapse,
  CircularProgress,
  Badge,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  Dashboard as DashboardIcon,
  BarChart as AnalyticsIcon,
  Videocam as CameraIcon,
  Today as DailyAttendanceIcon,
  Timeline as EmployeeTimelineIcon,
  Assessment as ReportsIcon,
  Person as PersonIcon,
  Business as DepartmentIcon,
  CalendarMonth as CalendarIcon,
  Settings as SettingsIcon,  Devices as EdgeDeviceIcon,
  Face as FaceValidationIcon,
  BeachAccess as VacationIcon,
  Event as EventIcon,
  Policy as PolicyIcon,
  Notifications as NotificationsIcon,
  Lock as LockIcon,
  Shield as PPEIcon,
  Build as BuildIcon,
  Collections as CollectionsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import attendnowLogo from '../assets/attendlogo.png';
import dataMindLogo from '../assets/DataMind_Logo.png';
import CompanySelector from './CompanySelector';
import { buildCompanyUrl } from '../utils/navigation';
import { payrollAPI } from '../utils/api';

const drawerWidth = 272;

/** True on PPE hub, PPE sub-routes, PPE violations, or department PPE settings (under PPE in nav). */
function isPpeSidebarSection(pathname: string): boolean {
  return (
    pathname.includes('/ppe/') ||
    pathname.includes('/ppe-violations') ||
    pathname.includes('/settings/ppe-settings') ||
    /\/company\/[^/]+\/ppe$/.test(pathname)
  );
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { user, logout, currentCompany, companies } = useAuth();
  const { 
    canViewDashboard, 
    canManageCameras, 
    canManageEmployees, 
    canManageDepartments, 
    canManageCalendars, 
    canViewReports, 
    canViewPPE,
    canViewPPESummary,
    canValidateFaces, 
    canManageEdgeDevices,
    canManageVacations,
    canManagePolicies,
    canAccessSettings,
    canAccessPpeSettings,
    canAccessPpeViolations,
    canManageRoles,
    canAccessUserManagement,
    canAssignUsers,
    canAccessCompanyManagement,
    canAccessHierarchy,
    canViewCustomReports,
    canManageHolidays,
    canViewAnalytics,
    canViewSnapshots,
    canAccessSnapshotSettings,
    canManageShifts,
    canViewPayroll,
    isSuperAdmin
  } = useRolePermissions();

  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [ppeOpen, setPpeOpen] = React.useState(false);
  const [visionIQOpen, setVisionIQOpen] = React.useState(false);
  const [payrollOpen, setPayrollOpen] = React.useState(false);
  const [payrollBlocking, setPayrollBlocking] = React.useState(0);
  const [loggingOut, setLoggingOut] = React.useState(false);

  const isPayrollEnabled = !!currentCompany?.effective_payroll_enabled;

  React.useEffect(() => {
    if (canViewPayroll && isPayrollEnabled && currentCompany?.company_id) {
      payrollAPI.getExceptionsSummary(currentCompany.company_id).then(res => {
        if (res.success) setPayrollBlocking(res.data.blocking || 0);
      }).catch(() => {});
    } else {
      setPayrollBlocking(0);
    }
  }, [canViewPayroll, isPayrollEnabled, currentCompany?.company_id]);

  const navButtonSx = {
    color: 'var(--an-sidebar-text)',
    borderRadius: 'var(--an-radius-md)',
    '& .MuiListItemIcon-root': {
      color: 'rgb(var(--an-sidebar-text-rgb) / 0.72)',
    },
    '&:hover': {
      backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.08)',
    },
    '&.Mui-selected': {
      color: 'var(--an-white)',
      backgroundColor: 'var(--an-sidebar-active-bg)',
      '& .MuiListItemIcon-root': {
        color: 'var(--an-accent)',
      },
      '&:hover': {
        backgroundColor: 'rgb(var(--an-accent-rgb) / 0.22)',
      },
    },
    '&.Mui-disabled': {
      opacity: 0.45,
      color: 'rgb(var(--an-sidebar-text-rgb) / 0.45)',
    },
  };

  const navSubButtonSx = {
    ...navButtonSx,
    py: 0.55,
    fontSize: '0.85rem',
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSettingsOpen(!settingsOpen);
  };

  const handlePPEClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPpeOpen(!ppeOpen);
  };

  const handleVisionIQClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVisionIQOpen(!visionIQOpen);
  };

  const handlePayrollClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPayrollOpen(!payrollOpen);
  };

  const isPayrollSidebarSection = (): boolean => {
    return location.pathname.includes('/payroll/');
  };

  React.useEffect(() => {
    const settingsPaths = [
      'settings/hierarchy',
      'settings/roles', 
      'settings/users', 
      'settings/assignments', 
      'settings/companies', 
      'settings/payroll-overview',
      'settings/groups',
      'settings/locations',
      'settings/appearance-thresholds'
    ];
    const isSettingsSubPage = settingsPaths.some(path => location.pathname.includes(path));
    if (isSettingsSubPage) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (isPpeSidebarSection(location.pathname)) {
      setPpeOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    const visionIQPaths = ['snapshots', 'desk-occupancy', 'settings/snapshot-settings', 'settings/desk-zones', 'settings/analysis-prompts'];
    const isVisionIQSubPage = visionIQPaths.some(path => location.pathname.includes(path));
    if (isVisionIQSubPage) {
      setVisionIQOpen(true);
    }
  }, [location.pathname]);

  const baseMenuItemsBeforeVisionIQ = [
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: 'dashboard', canAccess: canViewDashboard },
    { text: t('nav.analytics'), icon: <AnalyticsIcon />, path: 'analytics', canAccess: canViewAnalytics },
    { text: t('nav.edgeDevices'), icon: <EdgeDeviceIcon />, path: 'edge-devices', canAccess: canManageEdgeDevices },
    { text: t('nav.cameras'), icon: <CameraIcon />, path: 'cameras', canAccess: canManageCameras },
  ];

  const baseMenuItemsAfterVisionIQAbovePpe = [
    { text: t('nav.calendars'), icon: <CalendarIcon />, path: 'calendars', canAccess: canManageCalendars },
    { text: t('nav.scheduling'), icon: <i className="bi bi-calendar2-week" style={{ fontSize: '1.4rem' }} />, path: 'scheduling', canAccess: canManageShifts && !!currentCompany?.effective_shifts_enabled },
    { text: t('nav.departments'), icon: <DepartmentIcon />, path: 'departments', canAccess: canManageDepartments },
    { text: t('nav.employees'), icon: <PersonIcon />, path: 'employees', canAccess: canManageEmployees },
    { text: t('nav.reports'), icon: <ReportsIcon />, path: 'reports', canAccess: canViewReports },
    { text: t('nav.customReports'), icon: <BuildIcon />, path: 'custom-reports', canAccess: canViewCustomReports },
    { text: t('nav.vacations'), icon: <VacationIcon />, path: 'vacations', canAccess: canManageVacations },
    { text: t('nav.holidays'), icon: <EventIcon />, path: 'holidays', canAccess: canManageHolidays },
    { text: t('nav.faceValidation'), icon: <FaceValidationIcon />, path: 'face-validation', canAccess: canValidateFaces },
    { text: t('nav.dailyAttendance'), icon: <DailyAttendanceIcon />, path: 'daily-attendance', canAccess: canViewReports },
    { text: t('nav.employeeTimeline'), icon: <EmployeeTimelineIcon />, path: 'employee-timeline', canAccess: canViewReports },
  ];

  const baseMenuItemsAbovePpe = [
    ...baseMenuItemsBeforeVisionIQ,
    ...baseMenuItemsAfterVisionIQAbovePpe,
  ];

  const baseMenuItemsBelowPpe = [
    { text: t('nav.policies'), icon: <PolicyIcon />, path: 'policies', canAccess: canManagePolicies },
    { text: t('nav.notifications'), icon: <NotificationsIcon />, path: 'notifications', canAccess: canViewReports },
  ];

  const baseMenuItems = [...baseMenuItemsAbovePpe, ...baseMenuItemsBelowPpe];

  const settingsSubItems = [
    { text: t('nav.companyManagement'), path: 'settings/companies', canAccess: canAccessCompanyManagement },
    { text: t('nav.organizationHierarchy'), path: 'settings/hierarchy', canAccess: canAccessHierarchy },
    { text: t('nav.appearanceThresholds'), path: 'settings/appearance-thresholds', canAccess: canAccessCompanyManagement },
    { text: t('nav.roleManagement'), path: 'settings/roles', canAccess: canManageRoles },
    { text: t('nav.userManagement'), path: 'settings/users', canAccess: canAccessUserManagement },
    { text: t('nav.userCompanyAssignments'), path: 'settings/assignments', canAccess: canAssignUsers },
    { text: t('nav.payrollOverview'), path: 'settings/payroll-overview', canAccess: isSuperAdmin },
  ];

  const ppeSubItems = [
    { text: t('nav.ppeDetections'), path: 'ppe/detections', canAccess: canViewPPE },
    { text: t('nav.ppeSummary'), path: 'ppe/summary', canAccess: canViewPPESummary },
    { text: t('nav.ppeViolations'), path: 'ppe-violations', canAccess: canAccessPpeViolations },
    { text: t('nav.ppeDepartmentSettings'), path: 'settings/ppe-settings', canAccess: canAccessPpeSettings },
  ];

  const visionIQSubItems = [
    { text: t('nav.snapshots'), path: 'snapshots', canAccess: canViewSnapshots },
    { text: t('nav.deskOccupancy'), path: 'desk-occupancy', canAccess: canViewSnapshots },
    { text: t('nav.snapshotSettings'), path: 'settings/snapshot-settings', canAccess: canAccessSnapshotSettings },
    { text: t('nav.deskZones'), path: 'settings/desk-zones', canAccess: canViewSnapshots },
    { text: t('nav.analysisPrompts'), path: 'settings/analysis-prompts', canAccess: isSuperAdmin },
  ];

  const payrollSubItems = [
    { text: t('nav.payRuns'), path: 'payroll/periods', canAccess: canViewPayroll },
    { text: t('nav.exceptionInbox'), path: 'payroll/exceptions', canAccess: canViewPayroll },
    { text: t('nav.payComponents'), path: 'payroll/pay-components', canAccess: canViewPayroll },
    { text: t('nav.payrollSettings'), path: 'payroll/settings', canAccess: canViewPayroll },
  ];

  const canAccessPayrollMenu = canViewPayroll && isPayrollEnabled;
  const filteredPayrollSubItems = payrollSubItems.filter(item => item.canAccess);

  const canAccessPPEMenu =
    canViewPPE || canViewPPESummary || canAccessPpeViolations || canAccessPpeSettings;

  const canAccessVisionIQMenu =
    canViewSnapshots || canAccessSnapshotSettings || isSuperAdmin;

  // Filter menu items based on permissions
  const menuItems = baseMenuItems.filter(item => item.canAccess);

  // Filter menu items for users with no companies
  const availableMenuItems = !currentCompany && companies.length === 0
    ? menuItems.filter(item => {
        // Only show Dashboard for users with dashboard permissions
        if (item.path === 'dashboard') return canViewDashboard;
        return false;
      })
    : menuItems;

  const pathsAbovePpe = new Set(baseMenuItemsAbovePpe.map(item => item.path));
  const availableMenuItemsAbovePpe = availableMenuItems.filter(item => pathsAbovePpe.has(item.path));
  const availableMenuItemsBelowPpe = availableMenuItems.filter(item => !pathsAbovePpe.has(item.path));
  const pathsBeforeVisionIQ = new Set(baseMenuItemsBeforeVisionIQ.map(item => item.path));
  const availableMenuItemsBeforeVisionIQ = availableMenuItemsAbovePpe.filter(item => pathsBeforeVisionIQ.has(item.path));
  const availableMenuItemsAfterVisionIQ = availableMenuItemsAbovePpe.filter(item => !pathsBeforeVisionIQ.has(item.path));

  const isSettingsSidebarSection = () => {
    const settingsPaths = [
      'settings/hierarchy',
      'settings/roles',
      'settings/users',
      'settings/assignments',
      'settings/companies',
      'settings/payroll-overview',
      'settings/groups',
      'settings/locations',
      'settings/appearance-thresholds',
    ];
    return settingsPaths.some(path => location.pathname.includes(path));
  };

  const isVisionIQSidebarSection = () => {
    const visionIQPaths = ['snapshots', 'desk-occupancy', 'settings/snapshot-settings', 'settings/desk-zones', 'settings/analysis-prompts'];
    return visionIQPaths.some(path => location.pathname.includes(path));
  };

  React.useEffect(() => {
    if (isPayrollSidebarSection()) {
      setPayrollOpen(true);
    }
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    if (path === 'settings/payroll-overview') {
      navigate('/settings/payroll-overview');
      onClose();
      return;
    }
    if (currentCompany) {
      navigate(buildCompanyUrl(currentCompany.company_id, path));
    } else if (companies.length === 0) {
      // User has no companies
      if (path === 'dashboard' && canViewDashboard) {
        navigate(`/no-company/${path}`);
      } else if (
        (path === 'settings' || path.startsWith('settings/')) &&
        (canAccessSettings || (path === 'settings/ppe-settings' && canAccessPpeSettings))
      ) {
        navigate(`/${path}`);
      }
    } else {
      // User has companies but none selected - redirect to first company
      const firstCompany = companies[0];
      navigate(buildCompanyUrl(firstCompany.company_id, path));
    }
    onClose();
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } finally {
      setLoggingOut(false);
    }
  };

  // Helper function to check if a menu item is currently active
  const isMenuItemActive = (menuPath: string) => {
    if (currentCompany) {
      const expectedPath = buildCompanyUrl(currentCompany.company_id, menuPath);
      return location.pathname === expectedPath;
    } else if (companies.length === 0) {
      // Check no-company routes
      if (menuPath === 'dashboard' && canViewDashboard) {
        const expectedPath = `/no-company/${menuPath}`;
        return location.pathname === expectedPath;
      } else if (
        (menuPath === 'settings' || menuPath.startsWith('settings/')) &&
        (canAccessSettings || (menuPath === 'settings/ppe-settings' && canAccessPpeSettings))
      ) {
        return location.pathname === `/${menuPath}`;
      }
    }
    return false;
  };


  const renderSectionLabel = (label: string) => (
    <Typography
      component="div"
      sx={{
        px: 1.5,
        pt: 1.25,
        pb: 0.75,
        color: 'rgb(var(--an-sidebar-text-rgb) / 0.52)',
        fontSize: '0.68rem',
        fontWeight: 800,
        letterSpacing: '0.08em',
        lineHeight: 1,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
  );
  const renderMenuItems = (items: typeof baseMenuItems) => items.map((item) => (
    <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
      <ListItemButton
        onClick={() => handleNavigation(item.path)}
        selected={isMenuItemActive(item.path)}
        disabled={!currentCompany && companies.length > 0}
        sx={navButtonSx}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            fontSize: '0.9rem',
            fontWeight: isMenuItemActive(item.path) ? 'medium' : 'normal',
          }}
        />
      </ListItemButton>
    </ListItem>
  ));

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--an-sidebar-bg)', color: 'var(--an-sidebar-text)' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          backgroundColor: 'var(--an-sidebar-bg)',
          color: 'var(--an-sidebar-text)',
          borderBottom: '1px solid rgb(var(--an-sidebar-text-rgb) / 0.10)',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <img 
            src={attendnowLogo} 
            alt="AttendNow Logo" 
            style={{
              maxWidth: '160px',
              height: 'auto',
              maxHeight: '40px',
            }}
          />
        </Box>
        
        {/* DataMind Branding */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-end',
            mb: 1,
            opacity: 0.9
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.75rem',
              fontWeight: 'light',
              mr: 0.5,
              color: 'rgb(var(--an-sidebar-text-rgb) / 0.72)'
            }}
          >
            {t('branding.byDataMind')}
          </Typography>
          <img 
            src={dataMindLogo} 
            alt="DataMind Logo" 
            style={{
              height: '40px',
              width: 'auto',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              mr: 2,
              bgcolor: 'rgb(var(--an-accent-rgb) / 0.18)',
            }}
          >
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {user?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgb(var(--an-sidebar-text-rgb) / 0.68)' }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
        
        {/* Company Selector */}
        <Box sx={{ mt: 2 }}>
          <CompanySelector />
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box className="an-sidebar-scroll" sx={{ flexGrow: 1, py: 2, overflowY: 'auto', overflowX: 'hidden' }}>
        <List sx={{ px: 2 }}>
          {renderMenuItems(availableMenuItemsBeforeVisionIQ)}

          {canAccessVisionIQMenu && (
            <>
              {renderSectionLabel(t('nav.sections.vision', { defaultValue: 'VisionIQ' }))}
            </>
          )}

          {canAccessVisionIQMenu && (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={handleVisionIQClick}
                  selected={isVisionIQSidebarSection()}
                  disabled={!currentCompany && companies.length > 0}
                  sx={navButtonSx}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CollectionsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.visionIQ')}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isVisionIQSidebarSection() ? 'medium' : 'normal',
                    }}
                  />
                  {visionIQOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={visionIQOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {visionIQSubItems.filter(item => item.canAccess).map((subItem) => (
                    <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                        selected={isMenuItemActive(subItem.path)}
                        sx={navSubButtonSx}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isMenuItemActive(subItem.path) ? 'medium' : 'normal',
                            color: isMenuItemActive(subItem.path) ? 'var(--an-white)' : 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {availableMenuItemsAfterVisionIQ.length > 0 && renderSectionLabel(t('nav.sections.operations', { defaultValue: 'Operations' }))}
          {renderMenuItems(availableMenuItemsAfterVisionIQ)}

          {/* PPE Collapsible Item (directly under Employee Timeline) */}
          {canAccessPPEMenu && (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={handlePPEClick}
                  selected={isPpeSidebarSection(location.pathname)}
                  disabled={!currentCompany && companies.length > 0}
                  sx={navButtonSx}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PPEIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.ppe')}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isPpeSidebarSection(location.pathname) ? 'medium' : 'normal',
                    }}
                  />
                  {ppeOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={ppeOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {ppeSubItems.filter(item => item.canAccess).map((subItem) => (
                    <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                        selected={isMenuItemActive(subItem.path)}
                        sx={navSubButtonSx}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isMenuItemActive(subItem.path) ? 'medium' : 'normal',
                            color: isMenuItemActive(subItem.path) ? 'var(--an-white)' : 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {canAccessPayrollMenu && filteredPayrollSubItems.length > 0 && (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={handlePayrollClick}
                  selected={isPayrollSidebarSection()}
                  disabled={!currentCompany && companies.length > 0}
                  sx={navButtonSx}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <i className="bi bi-cash-coin" style={{ fontSize: '1.4rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.payroll')}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isPayrollSidebarSection() ? 'medium' : 'normal',
                    }}
                  />
                  {payrollOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={payrollOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {filteredPayrollSubItems.map((subItem) => (
                    <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                        selected={isMenuItemActive(subItem.path)}
                        sx={navSubButtonSx}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isMenuItemActive(subItem.path) ? 'medium' : 'normal',
                            color: isMenuItemActive(subItem.path) ? 'var(--an-white)' : 'inherit',
                          }}
                        />
                        {subItem.path === 'payroll/exceptions' && payrollBlocking > 0 && (
                          <Badge badgeContent={payrollBlocking} color="error" sx={{ ml: 1 }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {availableMenuItemsBelowPpe.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isMenuItemActive(item.path)}
                disabled={!currentCompany && companies.length > 0}
                sx={navButtonSx}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isMenuItemActive(item.path) ? 'medium' : 'normal',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

          {/* Settings Collapsible Item */}
          {canAccessSettings && (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={handleSettingsClick}
                  selected={isSettingsSidebarSection()}
                  disabled={!currentCompany && companies.length > 0}
                  sx={navButtonSx}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.settings')}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isSettingsSidebarSection() ? 'medium' : 'normal',
                    }}
                  />
                  {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {settingsSubItems.filter(item => item.canAccess).map((subItem) => (
                    <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                        selected={isMenuItemActive(subItem.path)}
                        sx={navSubButtonSx}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isMenuItemActive(subItem.path) ? 'medium' : 'normal',
                            color: isMenuItemActive(subItem.path) ? 'var(--an-white)' : 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>
      </Box>

      {/* Logout Button */}
      <Box sx={{ p: 2 }}>
        <Divider sx={{ mb: 2, borderColor: 'rgb(var(--an-sidebar-text-rgb) / 0.12)' }} />
        <ListItemButton
          onClick={() => {
            navigate('/account');
            onClose();
          }}
          selected={location.pathname === '/account'}
          sx={{ ...navButtonSx, mb: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LockIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('nav.changePassword')}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: location.pathname === '/account' ? 'medium' : 'normal',
            }}
          />
        </ListItemButton>
        <ListItemButton
          onClick={handleLogout}
          disabled={loggingOut}
          sx={{
            color: 'var(--an-error-light)',
            borderRadius: 'var(--an-radius-md)',
            '& .MuiListItemIcon-root': { color: 'inherit' },
            '&:hover': {
              backgroundColor: 'rgb(var(--an-error-rgb) / 0.16)',
              color: 'var(--an-white)',
            },
            '&.Mui-disabled': {
              opacity: 0.7,
              cursor: 'default',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            {loggingOut ? <CircularProgress size={20} color="inherit" /> : <i className="bi bi-box-arrow-right"></i>}
          </ListItemIcon>
          <ListItemText
            primary={loggingOut ? t('loading.default') : t('nav.logout')}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 'medium',
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            color: 'var(--an-sidebar-text)',
            backgroundColor: 'var(--an-sidebar-bg)',
            boxShadow: 'var(--an-shadow-lg)',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            color: 'var(--an-sidebar-text)',
            backgroundColor: 'var(--an-sidebar-bg)',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </>
  );
};

export default Sidebar;


